import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { BarRounded } from '@visx/shape';
import { useState } from 'react';
import { formatCompactNumber } from '../lib/format.ts';
import { ChartFrame, ChartTooltip } from './ChartFrame.tsx';
import type { ChartBaseProps } from './ChartFrame.tsx';
import { OTHER_KEY, colourFor, slotColour } from './palette.ts';

export interface BarDatum {
  key: string;
  label: string;
  value: number;
}

export interface BarChartProps extends ChartBaseProps {
  data: BarDatum[];
  format?: (n: number) => string;
  /** Show at most this many bars; the rest fold into "Other" (default 8). */
  limit?: number;
  /**
   * One colour for every bar (the first data slot) unless the bars are series that appear
   * elsewhere on the page in their own colours; then pass `order`.
   */
  order?: string[];
  /** Keep the given order instead of sorting by value. */
  keepOrder?: boolean;
}

const ROW = 28;
const LABEL_W = 132;
const VALUE_W = 64;

/**
 * How items compare (data-viz.md): horizontal bars sorted by value, starting at zero, 2 px
 * apart with 2 px rounded ends, the label on the left and the value at the end of each bar.
 */
export function BarChart({ data, format = formatCompactNumber, limit = 8, order, keepOrder, height, ...base }: BarChartProps) {
  const [hover, setHover] = useState<string | null>(null);
  const sorted = keepOrder ? [...data] : [...data].sort((a, b) => b.value - a.value);
  const rows =
    sorted.length > limit
      ? [...sorted.slice(0, limit - 1), { key: OTHER_KEY, label: `Other (${sorted.length - limit + 1})`, value: sorted.slice(limit - 1).reduce((n, d) => n + d.value, 0) }]
      : sorted;
  const h = height ?? Math.max(ROW * 2, rows.length * ROW);
  const table = { columns: ['Item', 'Value'], rows: rows.map((d) => [d.label, format(d.value)]) };

  return (
    <ChartFrame {...base} height={h} isEmpty={rows.length === 0} table={table}>
      {(width) => {
        const iw = Math.max(0, width - LABEL_W - VALUE_W);
        const y = scaleBand<string>({ domain: rows.map((d) => d.key), range: [0, h], paddingInner: 2 / ROW, paddingOuter: 0 });
        const max = Math.max(0, ...rows.map((d) => d.value));
        const x = scaleLinear<number>({ domain: [0, max || 1], range: [0, iw] });
        const bh = Math.min(20, y.bandwidth());
        const hovered = rows.find((d) => d.key === hover);
        return (
          <>
            <svg width={width} height={h} className="overflow-visible">
              {rows.map((d) => {
                const top = (y(d.key) ?? 0) + (y.bandwidth() - bh) / 2;
                const colour = d.key === OTHER_KEY ? colourFor(OTHER_KEY, []) : order ? colourFor(d.key, order) : slotColour(0);
                const w = Math.max(d.value > 0 ? 2 : 0, x(Math.max(0, d.value)));
                return (
                  <Group key={d.key} top={0} onMouseEnter={() => setHover(d.key)} onMouseLeave={() => setHover(null)}>
                    <text x={LABEL_W - 8} y={top + bh / 2 + 4} textAnchor="end" fill="var(--fg-muted)" fontSize={12} fontFamily="var(--font-sans)">
                      {truncate(d.label, 20)}
                    </text>
                    <BarRounded x={LABEL_W} y={top} width={w} height={bh} radius={2} right fill={colour} opacity={hover && hover !== d.key ? 0.3 : 1} data-bar={d.key} />
                    <text x={LABEL_W + w + 6} y={top + bh / 2 + 4} fill="var(--fg)" fontSize={12} fontFamily="var(--font-sans)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {format(d.value)}
                    </text>
                    <rect x={0} y={y(d.key) ?? 0} width={width} height={y.bandwidth()} fill="transparent" />
                  </Group>
                );
              })}
            </svg>
            {hovered && (
              <ChartTooltip
                width={width}
                left={LABEL_W + x(Math.max(0, hovered.value))}
                top={(y(hovered.key) ?? 0) + y.bandwidth() / 2}
                heading={hovered.label}
                rows={[{ key: hovered.key, label: 'Value', colour: order ? colourFor(hovered.key, order) : slotColour(0), value: format(hovered.value) }]}
              />
            )}
          </>
        );
      }}
    </ChartFrame>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
